import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';

import { FormsModule } from '@angular/forms';
import { MatStepperModule, MatStepper } from '@angular/material/stepper';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatChipsModule } from '@angular/material/chips';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatExpansionModule } from '@angular/material/expansion';
import { debounceTime, Subject, Subscription, of } from 'rxjs';
import { catchError, map, switchMap, takeUntil } from 'rxjs/operators';
import { checkMultiplePathVersions } from './path-version-check';
import { ComponentDataSourceService } from '../../../services/component-data-source/component-data-source.service';
import { ComponentDataDestService } from '../../../services/component-data-dest/component-data-dest.service';
import {
  ReportWizardApiService,
  FilterModuleItem,
  FilterUserItem,
  PATH_VERSIONS_REQUEST_CONCURRENCY,
} from '../../../services/duna/report/report-wizard-api.service';
import { PdfDownloadService } from '../../../services/duna/report/pdf-download.service';
import {
  AdviDTO,
  AdviIntervalDTO,
  ChartType,
  ReportConfig,
  ReportTypeDefinition,
  applyTypeDefaults,
  createDefaultReportConfig,
  migrateLegacyAdviConfig,
} from '../../../dto/advi/advi.dto';
import { EducationalPathDTO } from '../../../dto/educational-path/educational-path.dto';
import { createDefaultListRequest } from '../../../dto/list-request.dto';
import {
  ReportWizardContext,
  isStationMode,
  showAdviseringStep,
  showLocationStep,
  showGdprStep,
  canSave,
} from './report-wizard-context';
import { REPORT_TYPE_DEFINITIONS } from '../../../services/duna/report/report-type-registry';

@Component({
  selector: 'app-duna-report-wizard',
  standalone: true,
  imports: [
    FormsModule,
    MatStepperModule,
    MatButtonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatCardModule,
    MatRadioModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    MatChipsModule,
    MatSlideToggleModule,
    MatExpansionModule,
  ],
  templateUrl: './duna-report-wizard.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./duna-report-wizard.component.scss'],
})
export class DunaReportWizardComponent implements OnInit, OnDestroy {
  /** Cached version counts per path guid; lives only as long as this wizard instance. */
  private readonly pathVersionCounts = new Map<string, number>();
  /** Emits the path guids to check; switchMap cancels a superseded check. */
  private readonly pathVersionCheck$ = new Subject<string[]>();
  private readonly destroy$ = new Subject<void>();
  private modulesSub?: Subscription;

  @Input() context!: ReportWizardContext;
  @Output() cancelled = new EventEmitter<void>();
  @Output() saved = new EventEmitter<AdviDTO>();
  @Output() generated = new EventEmitter<void>();

  @ViewChild('stepper') stepper!: MatStepper;

  reportTypes: ReportTypeDefinition[] = REPORT_TYPE_DEFINITIONS;
  selectedTypeDef: ReportTypeDefinition | null =
    REPORT_TYPE_DEFINITIONS[0] ?? null;
  loadingTypes = true;
  typesLoadError = '';
  config: ReportConfig = createDefaultReportConfig();
  name = '';
  description = '';
  gdprAccepted = false;
  isGenerating = false;
  isSaving = false;

  availablePaths: EducationalPathDTO[] = [];
  availableLocations: { guid: string; name: string }[] = [];
  availableModules: FilterModuleItem[] = [];
  availableUsers: FilterUserItem[] = [];
  availableGroups: { group_id: number; name: string }[] = [];

  groupsEnabled = false;
  showPathVersionChoice = false;

  allPathsSelected = true;
  allModulesSelected = true;
  allLocationsSelected = true;
  allGroupsSelected = true;
  allUsersSelected = true;

  pathSearch = '';
  pathPageIndex = 0;
  pathPageSize = 25;
  pathTotalPages = 1;
  pathRangeStart = 0;
  pathRangeEnd = 0;
  filteredPaths: EducationalPathDTO[] = [];
  paginatedPaths: EducationalPathDTO[] = [];

  moduleSearch = '';
  modulePageIndex = 0;
  modulePageSize = 25;
  moduleTotalPages = 1;
  moduleRangeStart = 0;
  moduleRangeEnd = 0;
  filteredModules: FilterModuleItem[] = [];
  paginatedModules: FilterModuleItem[] = [];

  locationSearch = '';
  locationPageIndex = 0;
  locationPageSize = 25;
  locationTotalPages = 1;
  locationRangeStart = 0;
  locationRangeEnd = 0;
  filteredLocations: { guid: string; name: string }[] = [];
  paginatedLocations: { guid: string; name: string }[] = [];

  groupSearch = '';
  groupPageIndex = 0;
  groupPageSize = 25;
  groupTotalPages = 1;
  groupRangeStart = 0;
  groupRangeEnd = 0;
  filteredGroups: { group_id: number; name: string }[] = [];
  paginatedGroups: { group_id: number; name: string }[] = [];

  userSearch = '';
  userPageIndex = 0;
  userPageSize = 25;
  userTotalPages = 1;
  userRangeStart = 0;
  userRangeEnd = 0;
  filteredUsers: FilterUserItem[] = [];
  paginatedUsers: FilterUserItem[] = [];
  private userSearch$ = new Subject<string>();
  loadingModules = false;
  loadingUsers = false;

  readonly pageSizeOptions = [10, 25, 50, 100];

  currentYear = new Date().getFullYear();
  intervalUnits = [
    { value: 'days', label: 'dage' },
    { value: 'weeks', label: 'uger' },
    { value: 'months', label: 'måneder' },
  ];

  constructor(
    private api: ReportWizardApiService,
    private pdfDownload: PdfDownloadService,
    private dataSource: ComponentDataSourceService,
    private dataDest: ComponentDataDestService
  ) {}

  ngOnInit(): void {
    if (isStationMode(this.context) && this.context.locationGuid) {
      this.config.location_guids = [this.context.locationGuid];
    }
    if (this.context.initialConfig) {
      this.config = { ...this.context.initialConfig };
      if (!this.config.group_ids) {
        this.config.group_ids = [];
      }
      if (!this.config.path_version_mode) {
        this.config.path_version_mode = 'grouped';
      }
      this.syncSelectionFlagsFromConfig();
    }
    this.name = this.context.initialName || '';
    this.description = this.context.initialDescription || '';

    // Vis wizarden med det samme (client registry) — API opdaterer listen bagefter
    this.applyInitialReportType();

    this.userSearch$
      .pipe(debounceTime(300))
      .subscribe((q) => this.loadUsers(q));

    this.pathVersionCheck$
      .pipe(
        switchMap((guids) =>
          checkMultiplePathVersions(
            guids,
            this.pathVersionCounts,
            (guid) => this.api.getPathVersionCount(guid),
            PATH_VERSIONS_REQUEST_CONCURRENCY
          )
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((result) => {
        if (result === 'multiple') {
          this.showPathVersionChoice = true;
        } else if (result === 'single') {
          this.showPathVersionChoice = false;
          this.config.path_version_mode = 'grouped';
        } else if (this.config.path_version_mode === 'separated') {
          // Could not determine (request errors): keep the saved mode and keep the
          // choice visible so it is not silently reset.
          this.showPathVersionChoice = true;
        }
      });

    this.api.getReportTypes().subscribe({
      next: (types) => {
        this.reportTypes = this.mergeReportTypes(this.reportTypes, types);
        const def =
          this.reportTypes.find((t) => t.id === this.config.report_type) ??
          this.reportTypes[0];
        if (def) this.selectedTypeDef = def;
        this.loadingTypes = false;
        this.typesLoadError = '';
      },
      error: () => {
        this.loadingTypes = false;
        this.typesLoadError =
          'Kunne ikke hente rapporttyper fra server — viser standardliste.';
      },
    });

    this.loadPaths();
    this.loadGroupsEnabled();
    if (showLocationStep(this.context)) {
      this.loadLocations();
    } else {
      this.loadUsers();
    }
  }

  private applyInitialReportType(): void {
    const existing = this.reportTypes.find(
      (t) => t.id === this.config.report_type
    );
    if (existing) {
      this.selectedTypeDef = existing;
      if (!this.context.initialConfig) {
        this.config = applyTypeDefaults(
          { ...this.config, report_type: existing.id },
          existing
        );
      }
    } else if (this.reportTypes.length) {
      this.selectReportType(this.reportTypes[0]);
    }
  }

  /** Bevar stabile kort-tekster fra client registry når API returnerer. */
  private mergeReportTypes(
    current: ReportTypeDefinition[],
    fromApi: ReportTypeDefinition[]
  ): ReportTypeDefinition[] {
    const byId = new Map(current.map((t) => [t.id, t]));
    for (const apiType of fromApi) {
      const existing = byId.get(apiType.id);
      byId.set(
        apiType.id,
        existing
          ? {
              ...apiType,
              name: existing.name,
              description: existing.description,
            }
          : apiType
      );
    }
    return Array.from(byId.values());
  }

  get isStation(): boolean {
    return isStationMode(this.context);
  }
  get showLocations(): boolean {
    return showLocationStep(this.context);
  }
  get showGroups(): boolean {
    return this.groupsEnabled;
  }
  get showAdvisering(): boolean {
    return showAdviseringStep(this.context);
  }
  get showGdpr(): boolean {
    return showGdprStep(this.context);
  }
  get canSaveReport(): boolean {
    return canSave(this.context);
  }

  selectReportType(type: ReportTypeDefinition): void {
    const isNewType = this.config.report_type !== type.id;
    this.selectedTypeDef = type;
    if (isNewType) {
      this.config = applyTypeDefaults(
        { ...this.config, report_type: type.id },
        type
      );
    }
  }

  isSectionEnabled(id: string): boolean {
    return this.config.sections[id] !== false;
  }
  toggleSection(id: string, on: boolean): void {
    this.config.sections[id] = on;
  }

  isDimensionEnabled(id: string): boolean {
    if (this.config.dimensions[id] !== undefined) {
      return this.config.dimensions[id] !== false;
    }
    const def = this.selectedTypeDef?.dimensions.find((d) => d.id === id)?.default;
    return def !== false;
  }
  toggleDimension(id: string, on: boolean): void {
    this.config.dimensions[id] = on;
  }

  /** Current value of a select option; falls back to the type's default (e.g. older saved reports). */
  optionValue(id: string): string {
    return (
      this.config.options?.[id] ??
      this.selectedTypeDef?.options?.find((o) => o.id === id)?.default ??
      ''
    );
  }
  setOption(id: string, value: string): void {
    this.config.options = { ...(this.config.options ?? {}), [id]: value };
  }

  isChartEnabled(ct: ChartType): boolean {
    return this.config.charts.types.includes(ct);
  }
  toggleChart(ct: ChartType, on: boolean): void {
    if (on && !this.config.charts.types.includes(ct))
      this.config.charts.types.push(ct);
    else
      this.config.charts.types = this.config.charts.types.filter(
        (t) => t !== ct
      );
  }

  // ── Path selection ──
  private syncSelectionFlagsFromConfig(): void {
    if (this.config.path_ids?.length) this.allPathsSelected = false;
    if (this.config.module_ids?.length) this.allModulesSelected = false;
    if (this.config.location_guids?.length) this.allLocationsSelected = false;
    if (this.config.user_ids?.length) this.allUsersSelected = false;
    if (this.config.group_ids?.length) this.allGroupsSelected = false;
  }

  resolvePathId(path: EducationalPathDTO): number {
    return Number(
      path.path_id ?? (path as { id?: number }).id ?? path.base_path_id
    );
  }

  private selectedPathIds(): number[] {
    return this.allPathsSelected
      ? []
      : this.config.path_ids.map((id) => Number(id)).filter((id) => id > 0);
  }

  private maybeSelectAllPaths(): void {
    if (!this.availablePaths.length) return;
    const allIds = this.availablePaths.map((p) => this.resolvePathId(p));
    if (
      allIds.length > 0 &&
      allIds.every((id) => this.config.path_ids.includes(id))
    ) {
      this.allPathsSelected = true;
      this.config.path_ids = [];
    }
  }

  private maybeSelectAllModules(): void {
    if (!this.availableModules.length) return;
    const allIds = this.availableModules.map((m) => m.module_id);
    if (
      allIds.length > 0 &&
      allIds.every((id) => this.config.module_ids.includes(id))
    ) {
      this.allModulesSelected = true;
      this.config.module_ids = [];
    }
  }

  private maybeSelectAllLocations(): void {
    if (!this.availableLocations.length) return;
    const allGuids = this.availableLocations.map((l) => l.guid);
    if (
      allGuids.length > 0 &&
      allGuids.every((g) => this.config.location_guids.includes(g))
    ) {
      this.allLocationsSelected = true;
      this.config.location_guids = [];
    }
  }

  private maybeSelectAllUsers(): void {
    if (!this.availableUsers.length) return;
    const allIds = this.availableUsers.map((u) => u.id);
    if (
      allIds.length > 0 &&
      allIds.every((id) => this.config.user_ids.includes(id))
    ) {
      this.allUsersSelected = true;
      this.config.user_ids = [];
    }
  }

  private reconcileModuleSelection(): void {
    const valid = new Set(this.availableModules.map((m) => m.module_id));
    if (this.allModulesSelected) {
      this.config.module_ids = [];
      return;
    }
    this.config.module_ids = this.config.module_ids.filter((id) =>
      valid.has(id)
    );
    this.maybeSelectAllModules();
  }

  private reconcileUserSelection(): void {
    const valid = new Set(this.availableUsers.map((u) => u.id));
    if (this.allUsersSelected) {
      this.config.user_ids = [];
      return;
    }
    this.config.user_ids = this.config.user_ids.filter((id) => valid.has(id));
    this.maybeSelectAllUsers();
  }

  private onPathsChanged(): void {
    this.refreshPathVersionChoice();
    this.loadModules();
    this.loadUsers(this.userSearch);
  }

  onPathVersionModeChange(): void {
    this.loadModules();
  }

  private refreshPathVersionChoice(): void {
    const paths = this.allPathsSelected
      ? this.availablePaths
      : this.availablePaths.filter((p) =>
          this.selectedPathIds().includes(this.resolvePathId(p))
        );
    if (!paths.length) {
      // Cancels any check still running for a previous selection.
      this.pathVersionCheck$.next([]);
      this.showPathVersionChoice = false;
      return;
    }
    this.pathVersionCheck$.next(paths.map((p) => p.guid));
  }

  ngOnDestroy(): void {
    this.modulesSub?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private onLocationsChanged(): void {
    this.loadUsers(this.userSearch);
  }

  private onGroupsChanged(): void {
    this.loadUsers(this.userSearch);
  }

  private selectedGroupIds(): number[] {
    if (!this.groupsEnabled || this.allGroupsSelected) {
      return [];
    }
    return this.config.group_ids
      .map((id) => Number(id))
      .filter((id) => id > 0);
  }

  private loadGroupsEnabled(): void {
    this.dataSource.executeDataSource('duna_groups_enabled', {})?.subscribe({
      next: (res: { visible?: boolean }) => {
        this.groupsEnabled = res?.visible === true;
        if (this.groupsEnabled) {
          this.loadGroups();
        }
      },
      error: () => {
        this.groupsEnabled = false;
      },
    });
  }

  loadGroups(): void {
    this.dataSource
      .executeDataSource('duna_user_groups', {
        filters: [
          { column: 'pageIndex', value: 0, operator: '=' },
          { column: 'pageSize', value: 1000, operator: '=' },
        ],
      })
      ?.subscribe({
        next: (r: { items?: { group_id: number; name: string }[] }) => {
          this.availableGroups = (r?.items ?? [])
            .map((g) => ({
              group_id: Number(g.group_id),
              name: (g.name || `Gruppe ${g.group_id}`).trim(),
            }))
            .filter((g) => g.group_id > 0);
          this.maybeSelectAllGroups();
          this.refreshGroupList();
        },
        error: () => {
          this.availableGroups = [];
          this.refreshGroupList();
        },
      });
  }

  onGroupSearchChange(): void {
    this.groupPageIndex = 0;
    this.refreshGroupList();
  }

  setGroupPage(index: number): void {
    this.groupPageIndex = index;
    this.updatePaginatedGroups();
  }

  onGroupPageSizeChange(): void {
    this.groupPageIndex = 0;
    this.updatePaginatedGroups();
  }

  private refreshGroupList(): void {
    const q = this.groupSearch.trim().toLowerCase();
    this.filteredGroups = !q
      ? [...this.availableGroups]
      : this.availableGroups.filter((g) => g.name.toLowerCase().includes(q));
    this.updatePaginatedGroups();
  }

  private updatePaginatedGroups(): void {
    this.groupTotalPages = Math.max(
      1,
      Math.ceil(this.filteredGroups.length / this.groupPageSize)
    );
    if (this.groupPageIndex > this.groupTotalPages - 1) {
      this.groupPageIndex = Math.max(0, this.groupTotalPages - 1);
    }
    const start = this.groupPageIndex * this.groupPageSize;
    this.paginatedGroups = this.filteredGroups.slice(
      start,
      start + this.groupPageSize
    );
    this.groupRangeStart = this.filteredGroups.length ? start + 1 : 0;
    this.groupRangeEnd = Math.min(
      this.filteredGroups.length,
      start + this.groupPageSize
    );
  }

  private maybeSelectAllGroups(): void {
    if (!this.availableGroups.length) return;
    const allIds = this.availableGroups.map((g) => g.group_id);
    if (
      allIds.length > 0 &&
      allIds.every((id) => this.config.group_ids.includes(id))
    ) {
      this.allGroupsSelected = true;
      this.config.group_ids = [];
    }
  }

  toggleAllGroups(on: boolean): void {
    this.allGroupsSelected = on;
    this.config.group_ids = [];
    this.onGroupsChanged();
  }

  isGroupSelected(id: number): boolean {
    return this.allGroupsSelected || this.config.group_ids.includes(id);
  }

  toggleGroup(id: number, on: boolean): void {
    const groupId = Number(id);
    if (this.allGroupsSelected) {
      this.allGroupsSelected = false;
      if (on) {
        this.config.group_ids = [groupId];
      } else {
        this.config.group_ids = this.availableGroups
          .map((g) => g.group_id)
          .filter((gid) => gid !== groupId);
      }
    } else if (on) {
      if (!this.config.group_ids.includes(groupId)) {
        this.config.group_ids.push(groupId);
      }
    } else {
      this.config.group_ids = this.config.group_ids.filter((x) => x !== groupId);
    }
    this.maybeSelectAllGroups();
    this.onGroupsChanged();
  }

  loadPaths(): void {
    const req = createDefaultListRequest();
    req.pageSize = 10000;
    this.dataSource
      .executeDataSource('educational_paths', { listRequest: req })
      ?.subscribe((r: any) => {
        this.availablePaths = r?.items || r?.data || [];
        this.maybeSelectAllPaths();
        this.refreshPathList();
        // onPathsChanged() already runs refreshPathVersionChoice(); calling it here as
        // well doubled every /paths/{guid}/versions request (and its preflight).
        this.onPathsChanged();
      });
  }

  onPathSearchChange(): void {
    this.pathPageIndex = 0;
    this.refreshPathList();
  }

  setPathPage(index: number): void {
    this.pathPageIndex = index;
    this.updatePaginatedPaths();
  }

  onPathPageSizeChange(): void {
    this.pathPageIndex = 0;
    this.updatePaginatedPaths();
  }

  private refreshPathList(): void {
    const q = this.pathSearch.trim().toLowerCase();
    this.filteredPaths = !q
      ? [...this.availablePaths]
      : this.availablePaths.filter((p) =>
          (p.name || p.full_name || '').toLowerCase().includes(q)
        );
    this.updatePaginatedPaths();
  }

  private updatePaginatedPaths(): void {
    this.pathTotalPages = Math.max(
      1,
      Math.ceil(this.filteredPaths.length / this.pathPageSize)
    );
    if (this.pathPageIndex > this.pathTotalPages - 1) {
      this.pathPageIndex = Math.max(0, this.pathTotalPages - 1);
    }
    const start = this.pathPageIndex * this.pathPageSize;
    this.paginatedPaths = this.filteredPaths.slice(
      start,
      start + this.pathPageSize
    );
    this.pathRangeStart = this.filteredPaths.length ? start + 1 : 0;
    this.pathRangeEnd = Math.min(
      this.filteredPaths.length,
      start + this.pathPageSize
    );
  }

  isPathSelected(id: number): boolean {
    return this.allPathsSelected || this.config.path_ids.includes(id);
  }

  toggleAllPaths(on: boolean): void {
    this.allPathsSelected = on;
    this.config.path_ids = [];
    this.onPathsChanged();
  }

  togglePath(id: number, on: boolean): void {
    const pathId = Number(id);
    if (this.allPathsSelected) {
      this.allPathsSelected = false;
      if (on) {
        this.config.path_ids = [pathId];
      } else {
        this.config.path_ids = this.availablePaths
          .map((p) => this.resolvePathId(p))
          .filter((pid) => pid !== pathId);
      }
    } else if (on) {
      if (!this.config.path_ids.includes(pathId))
        this.config.path_ids.push(pathId);
    } else {
      this.config.path_ids = this.config.path_ids.filter((x) => x !== pathId);
    }
    this.maybeSelectAllPaths();
    this.onPathsChanged();
  }

  // ── Module selection ──
  loadModules(): void {
    const pathIds = this.selectedPathIds();
    if (!this.allPathsSelected && pathIds.length === 0) {
      this.modulesSub?.unsubscribe();
      this.availableModules = [];
      this.reconcileModuleSelection();
      this.refreshModuleList();
      this.loadingModules = false;
      return;
    }

    this.loadingModules = true;
    const apiPathIds = this.allPathsSelected ? [] : pathIds;
    const pathsForFallback = this.availablePaths
      .filter(
        (p) => this.allPathsSelected || pathIds.includes(this.resolvePathId(p))
      )
      .map((p) => ({ guid: p.guid }));

    const pathVersionMode = this.config.path_version_mode ?? 'grouped';
    const modules$ =
      apiPathIds.length === 0
        ? this.api.getFilterModules([], pathVersionMode)
        : pathsForFallback.length > 0 && pathVersionMode !== 'separated'
        ? this.api.loadModulesForPaths(pathsForFallback)
        : this.api.getFilterModules(apiPathIds, pathVersionMode);

    // A newer selection supersedes the previous module request (no stale overwrite).
    this.modulesSub?.unsubscribe();
    this.modulesSub = modules$.subscribe({
      next: (items) => {
        this.availableModules = items;
        this.reconcileModuleSelection();
        this.refreshModuleList();
        this.loadingModules = false;
      },
      error: () => {
        this.loadingModules = false;
      },
    });
  }

  onModuleSearchChange(): void {
    this.modulePageIndex = 0;
    this.refreshModuleList();
  }

  setModulePage(index: number): void {
    this.modulePageIndex = index;
    this.updatePaginatedModules();
  }

  onModulePageSizeChange(): void {
    this.modulePageIndex = 0;
    this.updatePaginatedModules();
  }

  private refreshModuleList(): void {
    const q = this.moduleSearch.trim().toLowerCase();
    this.filteredModules = !q
      ? [...this.availableModules]
      : this.availableModules.filter((m) => m.name.toLowerCase().includes(q));
    this.updatePaginatedModules();
  }

  private updatePaginatedModules(): void {
    this.moduleTotalPages = Math.max(
      1,
      Math.ceil(this.filteredModules.length / this.modulePageSize)
    );
    if (this.modulePageIndex > this.moduleTotalPages - 1) {
      this.modulePageIndex = Math.max(0, this.moduleTotalPages - 1);
    }
    const start = this.modulePageIndex * this.modulePageSize;
    this.paginatedModules = this.filteredModules.slice(
      start,
      start + this.modulePageSize
    );
    this.moduleRangeStart = this.filteredModules.length ? start + 1 : 0;
    this.moduleRangeEnd = Math.min(
      this.filteredModules.length,
      start + this.modulePageSize
    );
  }

  toggleAllModules(on: boolean): void {
    this.allModulesSelected = on;
    this.config.module_ids = [];
  }

  isModuleSelected(id: number): boolean {
    return this.allModulesSelected || this.config.module_ids.includes(id);
  }

  toggleModule(id: number, on: boolean): void {
    if (this.allModulesSelected) {
      this.allModulesSelected = false;
      if (on) {
        this.config.module_ids = [id];
      } else {
        this.config.module_ids = this.availableModules
          .map((m) => m.module_id)
          .filter((mid) => mid !== id);
      }
    } else if (on) {
      if (!this.config.module_ids.includes(id)) this.config.module_ids.push(id);
    } else {
      this.config.module_ids = this.config.module_ids.filter((x) => x !== id);
    }
    this.maybeSelectAllModules();
  }

  // ── Location selection ──
  loadLocations(): void {
    const req = createDefaultListRequest();
    req.pageSize = 10000;
    this.dataSource
      .executeDataSource('locations', { listRequest: req })
      ?.subscribe((r: any) => {
        this.availableLocations = (r?.items || r?.data || []).map((l: any) => ({
          guid: l.guid,
          name: l.locations_name || l.name || l.guid,
        }));
        this.maybeSelectAllLocations();
        this.refreshLocationList();
        this.loadUsers(this.userSearch);
      });
  }

  onLocationSearchChange(): void {
    this.locationPageIndex = 0;
    this.refreshLocationList();
  }

  setLocationPage(index: number): void {
    this.locationPageIndex = index;
    this.updatePaginatedLocations();
  }

  onLocationPageSizeChange(): void {
    this.locationPageIndex = 0;
    this.updatePaginatedLocations();
  }

  private refreshLocationList(): void {
    const q = this.locationSearch.trim().toLowerCase();
    this.filteredLocations = !q
      ? [...this.availableLocations]
      : this.availableLocations.filter((l) => l.name.toLowerCase().includes(q));
    this.updatePaginatedLocations();
  }

  private updatePaginatedLocations(): void {
    this.locationTotalPages = Math.max(
      1,
      Math.ceil(this.filteredLocations.length / this.locationPageSize)
    );
    if (this.locationPageIndex > this.locationTotalPages - 1) {
      this.locationPageIndex = Math.max(0, this.locationTotalPages - 1);
    }
    const start = this.locationPageIndex * this.locationPageSize;
    this.paginatedLocations = this.filteredLocations.slice(
      start,
      start + this.locationPageSize
    );
    this.locationRangeStart = this.filteredLocations.length ? start + 1 : 0;
    this.locationRangeEnd = Math.min(
      this.filteredLocations.length,
      start + this.locationPageSize
    );
  }

  toggleAllLocations(on: boolean): void {
    this.allLocationsSelected = on;
    this.config.location_guids = [];
    this.onLocationsChanged();
  }

  isLocationSelected(guid: string): boolean {
    return (
      this.allLocationsSelected || this.config.location_guids.includes(guid)
    );
  }

  toggleLocation(guid: string, on: boolean): void {
    if (this.allLocationsSelected) {
      this.allLocationsSelected = false;
      if (on) {
        this.config.location_guids = [guid];
      } else {
        this.config.location_guids = this.availableLocations
          .map((l) => l.guid)
          .filter((g) => g !== guid);
      }
    } else if (on) {
      if (!this.config.location_guids.includes(guid))
        this.config.location_guids.push(guid);
    } else {
      this.config.location_guids = this.config.location_guids.filter(
        (g) => g !== guid
      );
    }
    this.maybeSelectAllLocations();
    this.onLocationsChanged();
  }

  // ── User selection ──
  onUserSearchChange(): void {
    this.userPageIndex = 0;
    this.userSearch$.next(this.userSearch);
  }

  setUserPage(index: number): void {
    this.userPageIndex = index;
    this.updatePaginatedUsers();
  }

  onUserPageSizeChange(): void {
    this.userPageIndex = 0;
    this.updatePaginatedUsers();
  }

  private refreshUserList(): void {
    this.filteredUsers = [...this.availableUsers];
    this.updatePaginatedUsers();
  }

  private updatePaginatedUsers(): void {
    this.userTotalPages = Math.max(
      1,
      Math.ceil(this.filteredUsers.length / this.userPageSize)
    );
    if (this.userPageIndex > this.userTotalPages - 1) {
      this.userPageIndex = Math.max(0, this.userTotalPages - 1);
    }
    const start = this.userPageIndex * this.userPageSize;
    this.paginatedUsers = this.filteredUsers.slice(
      start,
      start + this.userPageSize
    );
    this.userRangeStart = this.filteredUsers.length ? start + 1 : 0;
    this.userRangeEnd = Math.min(
      this.filteredUsers.length,
      start + this.userPageSize
    );
  }

  loadUsers(search = ''): void {
    if (
      !isStationMode(this.context) &&
      !this.allLocationsSelected &&
      this.config.location_guids.length === 0
    ) {
      this.availableUsers = [];
      this.reconcileUserSelection();
      this.refreshUserList();
      this.loadingUsers = false;
      return;
    }

    this.loadingUsers = true;
    const guids =
      isStationMode(this.context) && this.context.locationGuid
        ? [this.context.locationGuid]
        : this.allLocationsSelected
        ? []
        : this.config.location_guids;

    const groupIds = this.selectedGroupIds();

    this.api
      .getFilterUsers(guids, search, groupIds.length ? groupIds : undefined)
      .pipe(
        switchMap((items) =>
          items.length > 0
            ? of(items)
            : this.api.loadUsersFallback(guids, search)
        ),
        catchError(() => this.api.loadUsersFallback(guids, search))
      )
      .subscribe({
        next: (items) => {
          this.availableUsers = items;
          this.reconcileUserSelection();
          this.refreshUserList();
          this.loadingUsers = false;
        },
        error: () => {
          this.loadingUsers = false;
        },
      });
  }

  toggleAllUsers(on: boolean): void {
    this.allUsersSelected = on;
    this.config.user_ids = [];
  }

  isUserSelected(id: number): boolean {
    return this.allUsersSelected || this.config.user_ids.includes(id);
  }

  toggleUser(id: number, on: boolean): void {
    if (this.allUsersSelected) {
      this.allUsersSelected = false;
      if (on) {
        this.config.user_ids = [id];
      } else {
        this.config.user_ids = this.availableUsers
          .map((u) => u.id)
          .filter((uid) => uid !== id);
      }
    } else if (on) {
      if (!this.config.user_ids.includes(id)) this.config.user_ids.push(id);
    } else {
      this.config.user_ids = this.config.user_ids.filter((x) => x !== id);
    }
    this.maybeSelectAllUsers();
  }

  // ── Delivery / advisering ──
  toggleAdvisering(on: boolean): void {
    this.config.delivery.advisering_enabled = on;
    if (on) {
      this.ensureCalendarInterval();
    }
  }

  /** Kalender-advisering gemmes i report_config.delivery.calendar_interval (cron). */
  get calendarInterval(): { value: number; unit: 'days' | 'weeks' | 'months' } {
    this.ensureCalendarInterval();
    return this.config.delivery.calendar_interval!;
  }

  private ensureCalendarInterval(): void {
    if (!this.config.delivery.calendar_interval) {
      this.config.delivery.calendar_interval = { value: 1, unit: 'months' };
    }
  }

  private resolvedAdvisoryIntervals(): AdviDTO['advisory_intervals'] {
    if (this.config.delivery.schedule_mode === 'calendar') {
      const cal = this.calendarInterval;
      return [{ value: cal.value, unit: cal.unit }];
    }
    return this.config.delivery.advisory_intervals;
  }

  addInterval(): void {
    this.config.delivery.advisory_intervals.push({ value: 1, unit: 'months' });
  }

  removeInterval(i: number): void {
    this.config.delivery.advisory_intervals.splice(i, 1);
  }

  isRecipientSelected(id: number): boolean {
    return this.config.delivery.recipient_user_ids.includes(id);
  }

  toggleRecipient(id: number, on: boolean): void {
    if (on) this.config.delivery.recipient_user_ids.push(id);
    else
      this.config.delivery.recipient_user_ids =
        this.config.delivery.recipient_user_ids.filter((x) => x !== id);
  }

  // ── Summary helpers ──
  summaryTypeName(): string {
    return this.selectedTypeDef?.name || '';
  }
  summaryPaths(): string {
    return this.allPathsSelected
      ? `Alle (${this.availablePaths.length})`
      : `${this.config.path_ids.length} valgt`;
  }
  summaryModules(): string {
    return this.allModulesSelected
      ? `Alle (${this.availableModules.length})`
      : `${this.config.module_ids.length} valgt`;
  }

  /** Eksplicit modul-liste til rapport — tom «alle» erstattes med faktiske IDs fra wizarden. */
  private resolvedModuleIds(): number[] {
    if (this.allModulesSelected) {
      return this.availableModules.map((m) => m.module_id);
    }
    return this.config.module_ids;
  }
  summaryLocations(): string {
    if (isStationMode(this.context))
      return this.context.locationName || 'Nuværende station';
    return this.allLocationsSelected
      ? 'Alle'
      : `${this.config.location_guids.length} valgt`;
  }
  summaryUsers(): string {
    return this.allUsersSelected
      ? 'Alle'
      : `${this.config.user_ids.length} valgt`;
  }
  summaryGroups(): string {
    return this.allGroupsSelected
      ? `Alle (${this.availableGroups.length})`
      : `${this.config.group_ids.length} valgt`;
  }
  summaryPathVersions(): string {
    return this.config.path_version_mode === 'separated'
      ? 'Hver version for sig'
      : 'Grupperet';
  }

  private buildModuleLabels(): Record<number, string> {
    const labels: Record<number, string> = {};
    for (const mod of this.availableModules) {
      const id = mod.module_id;
      const name = mod.name?.trim();
      if (id != null && name) {
        labels[id] = name;
      }
    }
    return labels;
  }

  buildPayload(): AdviDTO {
    const config =
      this.selectedTypeDef != null
        ? applyTypeDefaults(this.config, this.selectedTypeDef)
        : this.config;
    return {
      organizations_id: 0,
      name: this.name,
      description: this.description,
      path_ids: this.allPathsSelected
        ? this.availablePaths.map((p) => this.resolvePathId(p))
        : config.path_ids,
      report_type: config.report_type,
      report_config: {
        ...config,
        path_ids: this.allPathsSelected ? [] : config.path_ids,
        module_ids: this.resolvedModuleIds(),
        module_labels: this.buildModuleLabels(),
        location_guids:
          isStationMode(this.context) && this.context.locationGuid
            ? [this.context.locationGuid]
            : this.allLocationsSelected
            ? []
            : config.location_guids,
        user_ids: this.allUsersSelected ? [] : config.user_ids,
        group_ids: this.allGroupsSelected ? [] : config.group_ids,
      },
      recipient_user_ids: config.delivery.advisering_enabled
        ? config.delivery.recipient_user_ids
        : [],
      advisory_intervals: config.delivery.advisering_enabled
        ? this.resolvedAdvisoryIntervals()
        : [],
      is_active: true,
    };
  }

  saveReport(): void {
    if (!this.name.trim()) {
      alert('Angiv et navn');
      return;
    }
    this.isSaving = true;
    const payload = this.buildPayload();
    const dest = this.context.adviId
      ? this.dataDest.getDataDest('update_advisory')
      : this.dataDest.getDataDest('add_advisory');
    const call = this.context.adviId
      ? dest?.({ ...payload, advi_id: this.context.adviId })
      : dest?.(payload);
    call?.subscribe({
      next: (res: any) => {
        this.isSaving = false;
        this.saved.emit(res);
      },
      error: (err: unknown) => {
        this.isSaving = false;
        console.error(err);
        alert('Fejl ved gem.');
      },
    });
  }

  generateReport(): void {
    if (this.showGdpr && !this.gdprAccepted) {
      alert('Acceptér GDPR for at fortsætte.');
      return;
    }
    this.isGenerating = true;
    const payload = this.buildPayload();
    this.api
      .generateReport({
        report_config: payload.report_config,
        path_ids: payload.path_ids,
      })
      .subscribe({
        next: (res) => {
          this.isGenerating = false;
          if (res.success && res.pdf_content) {
            this.pdfDownload.downloadFromJsonResponse(res);
            this.generated.emit();
          } else {
            alert(res.message || 'Generering fejlede');
          }
        },
        error: (err) => {
          this.isGenerating = false;
          console.error(err);
          alert('Fejl ved generering af rapport.');
        },
      });
  }

  cancel(): void {
    this.cancelled.emit();
  }

  static contextFromAdvi(advi: AdviDTO): ReportWizardContext {
    return {
      mode: 'organization',
      adviId: advi.advi_id,
      initialConfig: migrateLegacyAdviConfig(advi),
      initialName: advi.name,
      initialDescription: advi.description,
    };
  }
}
